import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {setAccessToken} from "../slices/authSlice";
import {
    getLineageOriginContents,
    getLineageOriginContent,
    getLineageDebugInfo,
    getSimilarContentToVersion
} from "../api/client";
import "./Lineage.css"

import {ContentPageTile} from "./Pages";

import {Header} from "../components/Header";
import {setQueryString} from "../slices/usersSlice";
import {CenteredSpinner} from "../components/CenteredSpinner";
import {Toggle, Modal, Select, Button} from "@seismic/mantle";
import {HumanDate, ReadableDate} from "../components/Helpers";

const DistanceItem = ({name, distance}) => {
    if(distance === 0)
        return null;
    let distanceText = distance === 64 ? "??" : distance == null || distance === "" ? "N/A" : distance;
    return <span><b>{name}</b>: {distanceText}</span>;
}

const Distance = ({relativeTo, distance}) => {
    
    let color = "red";
    if(distance.totalDifference <= 15)
        color = "darkgreen";
    else if(distance.totalDifference <= 50)
        color = "darkorange";
    
    let distanceText = distance.totalDifference.toString();
    if(distance.differenceAverageHash === 64 || 
        distance.differencePerceptualHash === 64 || 
        distance.differenceDifferenceHash === 64 || 
        distance.differenceWaveletHash === 64 || 
        distance.differenceColorHash === 64 || 
        distance.differenceSimHash === 64)
        distanceText = "??";
    
    return distance.totalDifference > 0 && <div style={{textAlign: "center", backgroundColor: color, border: "1px solid #cfd4da", color: "white", padding: 8, borderRadius: 4}}>
        <h4 style={{fontWeight: "bold"}}>vs {relativeTo}</h4>
        <div><b>Total Difference</b>: {distanceText}</div>
        <div className="distance-items">
            <DistanceItem name="Avg" distance={distance.differenceAverageHash} />
            <DistanceItem name="Perc" distance={distance.differencePerceptualHash} />
            <DistanceItem name="Diff" distance={distance.differenceDifferenceHash} />
            <DistanceItem name="Wvlt" distance={distance.differenceWaveletHash}/>
            <DistanceItem name="Color" distance={distance.differenceColorHash} />
            <DistanceItem name="Text" distance={distance.differenceSimHash} />
        </div>
    </div>
}

const PageModalImage = ({thumbnailUrlCurrent, thumbnailUrlCompare, title}) => {

    const showOriginalOnly = thumbnailUrlCurrent === thumbnailUrlCompare;
    const [display, setDisplay] = useState("overlay");
    const showCurrent = display === "current" || display === "overlay";
    const showCompare = display === "compare" || display === "overlay";
    
    return <div>
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
            <div>vs {title}</div>
            {!showOriginalOnly && <div>
                <span>Select Display</span>
                <select style={{width: 200}} value={display} onChange={(e)=>setDisplay(e.target.value)}>
                    <option value="overlay">Overlay</option>
                    <option value="current">Current Only</option>
                    <option value="compare">{title} Only</option>
                </select>
            </div>}
        </div>
        <div style={{display: "flex", alignItems: "center", justifyContent: "center", width: "100%"}}>
            <div className="image-container">
                {showCurrent && <img src={thumbnailUrlCurrent} alt={title} className="background-image" />}
                {showCompare && <img src={thumbnailUrlCompare} alt={title} className={showCurrent ? "overlay-image" : "background-image" }/>}
            </div>
        </div>
    </div>
}

const PageModal = ({selectedPage, setSelectedPage}) => {

    if(selectedPage == null)
        return null;

    return <Modal
        open={true}
        size="xl"
        onClose={() => setSelectedPage(null)}
        header={selectedPage.name}
        closeOnOutsideClick>
        <div style={{display: "flex", gap: 16, paddingBottom: "16px"}}>
            {selectedPage.parentDistance.totalDifference === 0 && selectedPage.originDistance.totalDifference === 0 && <PageModalImage thumbnailUrlCurrent={selectedPage.thumbnailUrl} thumbnailUrlCompare={selectedPage.thumbnailUrl} title={selectedPage.name} />}
            {!selectedPage.parentHashSameAsOriginHash && selectedPage.parentDistance.totalDifference > 0 && <PageModalImage thumbnailUrlCurrent={selectedPage.thumbnailUrl} thumbnailUrlCompare={selectedPage.parentThumbnailUrl} title="Parent" />}
            {selectedPage.originDistance.totalDifference > 0 && <PageModalImage thumbnailUrlCurrent={selectedPage.thumbnailUrl} thumbnailUrlCompare={selectedPage.originThumbnailUrl} title="Original" />}
        </div>
    </Modal>
}

export const DebugPageModal = ({debugPage, setDebugPage}) => {

    const [isBusy, setIsBusy] = useState(false);
    const [error, setError] = useState(null);
    const [ccp, setCcp] = useState(null);
    const [cas, setCas] = useState(null);
    

    useEffect(() => {
        if(debugPage == null)
            return;
        setError(null);
        setCcp(null);
        setCas(null);
        setIsBusy(true);
        getLineageDebugInfo(debugPage.contentVersionId).then(data=> {
            setError(data.error);
            setCcp(data.ccp);
            setCas(data.cas);
            setIsBusy(false);
        });
    }, [debugPage]);
    
    if(debugPage == null)
        return null;
    
    let hasCcp = ccp != null;
    let isPptx = hasCcp && ccp.format === "PPTX";
    let hasPageLevelData = hasCcp && ccp.extractedText != null && ccp.extractedText.length > 0;
    let extractedTextLength = hasPageLevelData ? ccp.extractedText.length : null;

    let assetType4 = cas != null && cas.filter(a=>a.AssetType === 4);
    let hasAssetType4 = cas != null && cas.filter(a=>a.AssetType === 4).length >= 1;
    let hasMultipleAssetType4 = cas != null && cas.filter(a=>a.AssetType === 4).length >= 2;
    let hasPagePreviews = hasAssetType4 && cas.filter(a=>a.AssetType === 4)[0].PagePreviews != null;
    let asset4Length = hasPagePreviews ? cas.filter(a=>a.AssetType === 4)[0].PagePreviews.length : null;

    let hasSameNumberOfPages = extractedTextLength === asset4Length;
    
    let hasCas = cas != null;
    
    return <Modal
        open={true}
        size="xl"
        onClose={() => setDebugPage(null)}
        header={`${debugPage.name} - Debug Info for contentVersionId=${debugPage.contentVersionId} repository=${debugPage.repository}`}
        closeOnOutsideClick>
        <div>
            {isBusy && <CenteredSpinner />}
            {error && <div>
                <h3>Debug Data Errors</h3>
                <div style={{color: "red"}}>{error}</div>
                <hr />
            </div>}
            <div style={{width: "33%"}}>
                <h3>Other</h3>
                <ul>
                    <li>{hasSameNumberOfPages ? '✅' : '❌'} Has Same Page Count (CCP={extractedTextLength ?? "N/A"} = CAS={asset4Length ?? "N/A"})</li>
                </ul>
            </div>
            <div style={{display: "flex", justifyContent: "space-evenly", gap: 24}}>
                <div style={{width: "50%"}}>
                    <h3>CCP</h3>
                    <ul>
                        <li>{hasCcp ? '✅' : '❌'} Has CCP Data</li>
                        <li>{!hasCcp ? '❓' : (isPptx ?'✅' : '❌')} Is a PPTX File</li>
                        <li>{hasPageLevelData ? '✅' : '❌'} Has Page Level Data</li>
                        <div>&nbsp;</div>
                    </ul>
                    <div className="json-data">
                        <pre style={{whiteSpace: "pre-wrap", overflow: "hidden"}}>{JSON.stringify(ccp, null, 2)}</pre>
                    </div>
                </div>
                <div style={{width: "50%"}}>
                    <h3>CAS</h3>
                    <ul>
                        <li>{hasCas ? '✅' : '❌'} Has CAS Data</li>
                        <li>{hasAssetType4 ? '✅' : '❌'} Has AssetType = 4 Data</li>
                        <li>{hasMultipleAssetType4 ? '❌' : '✅'} Has Only One AssetType = 4</li>
                        <li>{hasPagePreviews ? '✅' : '❌'} Has Page Previews</li>
                    </ul>
                    <div className="json-data"><pre style={{whiteSpace: "pre-wrap", overflow: "hidden"}}>{JSON.stringify(cas, null, 2)}</pre>
                    </div>
                </div>
            </div>
        </div>
    </Modal>
}


const SimilarContentModal = ({similarContentPage, setSimilarContentPage}) => {

    const [similarContent, setSimilarContent] = useState([]);
    
    useEffect(() => {
        if(similarContentPage == null)
            return;
        setSimilarContent([]);
        getSimilarContentToVersion(similarContentPage?.contentVersionId, similarContentPage?.page)
            .then(data => {
                setSimilarContent(data);
            })
}, [JSON.stringify(similarContentPage)]);
    
    if(similarContentPage == null)
        return null;

    return <Modal
        open={true}
        size="xl"
        onClose={() => setSimilarContentPage(null)}
        header={`${similarContentPage.name} - Similar Content Page ${similarContentPage.page}`}
        closeOnOutsideClick>
        <div>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "8px", paddingRight: "6px"}}>
                <h3>Similar Content</h3>
                <Button label="Request Updates" onClick={()=>alert("This is for illustration purposes only.")} />
            </div>
            {similarContent.length === 0 && <div>No similar content found</div>}
            <div style={{display: "flex", flexWrap: "wrap", gap: 8}}>
                {similarContent.filter(a=>a.distance > 0 && a.distance <= 50 && a.repository == "Workspace").map(a=> <ContentPageTile showCheckmark={true} {...a} />)}
            </div>
        </div>
    </Modal>
}



const Page = ({page, onlyShowDifferences, setSelectedPage, setDebugPage, setSimilarContentPage}) => {
    
    const link = page.repository === "Library" ? `https://quake.seismic.com/app#/contentmanager/detail/${page.teamsiteId}/${page.contentId}/info/LIST/title?versionId=${page.contentVersionId}&structureId=undefined` : `https://quake.seismic.com/app#/workspace/doc/${page.contentId}//grid/title?viewType=MyFiles`;
    
    return <div style={{paddingLeft: page.level * 50, paddingTop: 8}}>
        {page.level === 0 && <><h2>Page {page.page}</h2><hr /></>}
        
        <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #cfd4da", borderRadius: 8, padding: 4, backgroundColor: "#ffffff"}}>
            <div style={{display: "flex", alignItems: "center", gap: "12px"}}>
                <img onClick={()=>setSelectedPage(page)} style={{cursor: "pointer"}} width={192} height={108} loading="lazy" src={page.thumbnailUrl} alt={page.name}/>
                <div>
                    <h3><a target="_blank" href={link}>{page.name} (Page {page.page})</a></h3>
                    <div><b>v{page.version ?? "??"}</b> created <u><HumanDate date={page.createdAt} /></u> by <a href={`mailto:${page.createdByEmail}`}>{page.createdBy ?? "Unknown"}</a> {page.isPublished === true && "(Published)"} {page.isPublished === false && "(Not Published)"} {page.isDeleted === true && "(Deleted)"}</div>
                    <div>{page.repository}</div>
                    <div>{page.id}</div>
                </div>
                <div>
                    <Distance relativeTo="Original" distance={page.originDistance} />
                </div>
                {!page.parentHashSameAsOriginHash && <div>
                    <Distance relativeTo="Parent" distance={page.parentDistance} />
                </div>}
            </div>
            <div>
                <a onClick={()=>setSimilarContentPage(page)}>Similar Content</a> | <a onClick={()=>setDebugPage(page)}>Debug</a>
            </div>
        </div>
        <>
            {page.children.filter(a=>(onlyShowDifferences && a.hasDifferentChildOrSelf) || !onlyShowDifferences).map(child => <Page key={child.id} page={child} setSimilarContentPage={setSimilarContentPage} setSelectedPage={setSelectedPage} onlyShowDifferences={onlyShowDifferences} setDebugPage={setDebugPage}/>)}
        </>
    </div>
}

export const Lineage = () => {

    const urlParams = new URLSearchParams(window.location.search);
    const qsContentId = urlParams.get('originLibraryContentVersionId');
    const dispatch = useDispatch();
    const [contentList, setContentList] = useState([]);
    const [selectedContentId, setSelectedContentId] = useState(null);
    const [content, setContent] = useState(null);
    const [onlyShowDifferences, setOnlyShowDifferences] = useState(true);
    const [selectedPage, setSelectedPage] = useState(null);
    const [debugPage, setDebugPage] = useState(null);
    const [similarContentPage, setSimilarContentPage] = useState(null);
    
    useEffect(() => {
        getLineageOriginContents()
            .then(data => {
                setContentList(data);
                if(data.filter(a=>a.id === qsContentId).length > 0)
                    setSelectedContentId(qsContentId);
                else
                    setSelectedContentId(data[0].id);
            })
    }, []);

    useEffect(() => {
        if (selectedContentId === null)
            return;
        setContent(null);
        getLineageOriginContent(selectedContentId)
            .then(data => {
                setContent(data)
            })
    }, [selectedContentId]);
    
    const signOut = () => {
        dispatch(setAccessToken(null));
    }
    
    const filteredContent = (content ?? []).filter(a=>(onlyShowDifferences && a.hasDifferentChildOrSelf) || !onlyShowDifferences);

    return <>
        <PageModal selectedPage={selectedPage} setSelectedPage={setSelectedPage} />
        <DebugPageModal debugPage={debugPage} setDebugPage={setDebugPage} />
        <SimilarContentModal similarContentPage={similarContentPage} setSimilarContentPage={setSimilarContentPage} />
        <Header signOut={signOut} title="Slide Lineage" navigateTo="/Lineage" />
        <div style={{padding: "16px"}}>
            <div style={{paddingBottom: 16, display: "flex", alignItems: "center", gap: 8}}>
                <div>
                    <select value={selectedContentId} onChange={(e) => {
                        setQueryString("originLibraryContentVersionId", e.target.value);
                        setSelectedContentId(e.target.value)
                    }
                    }>
                        {contentList.map(item => (
                            <option key={item.id} value={item.id}>
                                {item.name} ({item.count} references)
                            </option>
                        ))}
                    </select>
                </div>
                <div>Only show differences</div>
                <Toggle checked={onlyShowDifferences} onChange={event => setOnlyShowDifferences(event.target.checked)} />
            </div>
            {content === null && <CenteredSpinner />}
            {content && <div>
                {filteredContent.length === 0 && <div style={{padding:16, border: "1px solid #868e96", backgroundColor: "white", borderRadius: 8}}>No content to display. Try removing the 'Only show differences' filter?</div>}
                {filteredContent.map(a=> <Page key={a.id} page={a} setSimilarContentPage={setSimilarContentPage} onlyShowDifferences={onlyShowDifferences} setSelectedPage={setSelectedPage} setDebugPage={setDebugPage} />)}
            </div>}
        </div>
    </>;
}